import React, { useState, useEffect } from 'react';
import { Grid, Box } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import TeamV2MemberEditSidenav from './TeamV2MemberEditSidenav';
import TeamV2MemberAboutContainer from './TeamV2MemberAboutContainer';
import TeamV2MemberContactLinksContainer from './TeamV2MemberContactLinksContainer';
import TeamV2MemberPreview from './TeamV2MemberPreview';
import TeamV2LeadCaptureContainer from './TeamV2LeadCaptureContainer';
import TeamV2EmailSignatureContainer from './TeamV2EmailSignatureContainer';
import TeamV2VirtualBackgroundContainer from './TeamV2VirtualBackgroundContainer';

const TeamV2MemberEditDashboard = ({ user }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { memberId } = useParams();

    // Extract the section from the query parameter
    const queryParams = new URLSearchParams(location.search);
    const initialSection = queryParams.get('section') || 'about';

    // Check if this is a new member creation flow
    const isNewMember = location.pathname.includes('add-member');

    const [selectedItem, setSelectedItem] = useState(initialSection);

    // Mock team member data
    const [memberData, setMemberData] = useState({
        id: memberId || 'new-member-1',
        firstName: '',
        lastName: '',
        email: '',
        jobTitle: '',
        company: '',
        location: '',
        bio: '',
        phone: '',
        website: '',
        linkedin: '',
        profilePictureUrl: '',
        status: 'invited',
        role: 'member',
        department: '',
        createdAt: new Date().toISOString(),
        lastLogin: null,
        totalLogins: 0,
        contactLinks: [],
        isActive: true
    });

    // Handle URL management
    useEffect(() => {
        const section = queryParams.get('section');
        if (section) {
            setSelectedItem(section);
        }
    }, [location.search]);

    const handleMenuItemClick = (item) => {
        setSelectedItem(item);
        if (isNewMember) {
            navigate(`/team/add-member/${memberId}?section=${item}`);
        } else {
            navigate(`/team/members/edit/${memberId}?section=${item}`);
        }
    };

    // Render only specific menu items for new member
    const renderSidenav = () => {
        return (
            <TeamV2MemberEditSidenav
                onMenuItemClick={handleMenuItemClick}
                selectedItem={selectedItem}
                restrictedMode={isNewMember}
                hideOtherSections={isNewMember}
            />
        );
    };

    // Determine what to render based on the selected item
    const renderMainContent = () => {
        return (
            <>
                {/* Always render AboutContainer but conditionally show/hide */}
                <div style={{ display: selectedItem === 'about' ? 'block' : 'none' }}>
                    <TeamV2MemberAboutContainer
                        memberData={memberData}
                        setMemberData={setMemberData}
                        setSelectedItem={setSelectedItem}
                        isNewMember={isNewMember}
                    />
                </div>

                {/* Render other components conditionally */}
                {selectedItem === 'content' && <div>Content Container (Coming Soon)</div>}
                {selectedItem === 'touchCode' && <div>TouchCode Container (Coming Soon)</div>}
                {selectedItem === 'leadCapture' && (
                    <TeamV2LeadCaptureContainer
                        memberData={memberData}
                        setMemberData={setMemberData}
                        isNewMember={isNewMember}
                    />
                )}
                {selectedItem === 'virtualBackground' && (
                    <TeamV2VirtualBackgroundContainer
                        memberData={memberData}
                        setMemberData={setMemberData}
                        isNewMember={isNewMember}
                    />
                )}
                {selectedItem === 'emailSignature' && (
                    <TeamV2EmailSignatureContainer
                        memberData={memberData}
                        setMemberData={setMemberData}
                        isNewMember={isNewMember}
                    />
                )}
                {selectedItem === 'devices' && <div>Add Touch Devices Container (Coming Soon)</div>}
            </>
        );
    };

    const renderPreview = () => {
        return (
            <TeamV2MemberPreview
                memberData={memberData}
                isNewMember={isNewMember}
            />
        );
    };

    return (
        <Grid container sx={{ height: '100vh', padding: 1, overflow: 'hidden' }}>
            <Grid
                item
                xs={12}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                }}
            >
                {/* Sidebar - Left menu */}
                <Grid
                    item
                    xs={12}
                    sm={2.5}
                    md={2}
                    sx={{
                        borderRight: '1px solid #e0e0e0',
                        backgroundColor: '#ffffff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 1.5,
                        borderRadius: '8px 0 0 8px',
                    }}
                >
                    {renderSidenav()}
                </Grid>

                {/* Main content area */}
                <Grid
                    item
                    xs={12}
                    sm={9.5}
                    md={9}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 1.5,
                        backgroundColor: '#ffffff',
                        borderRadius: '0',
                        height: 'calc(100vh - 2 * 16px)',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        scrollbarWidth: 'thin',
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        {renderMainContent()}
                    </Box>
                </Grid>

                {/* Right-side member preview */}
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={5}
                    sx={{
                        padding: 1.5,
                        backgroundColor: '#ffffff',
                        borderRadius: '0 8px 8px 0',
                        borderLeft: '1px solid #e0e0e0',
                        height: '100%',
                    }}
                >
                    {renderPreview()}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TeamV2MemberEditDashboard;
